import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useRecoilValue } from 'recoil';

import { showWaitCursor } from 'components/common/Page/Page';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';
import Dialog from 'components/common/Dialog/Dialog';
import DropdownSelect from 'components/common/Dropdown/DropdownSelect';
import ELEMENT from 'common/element';
import * as K from 'common/constants';

import { DashboardConfigType, GraphConfigType } from 'components/dashboards/types';
import { SAVE_LAYOUT } from 'components/dashboards/queries';
import { setColumnLayouts, trimUnusedOptions } from 'components/dashboards/dashboardData';

import './SetColumns.scss';

/**
 * Modal dialog for editing a single dashboard graph JSON, in situ.
 *
 * @prop showModal          This modal is shown when true
 * @prop dashboardName      The dashboard config file name
 * @prop graphConfig        The JSON stanza for just this graph
 * @prop graphIndex         The index of this graph in the graphs array stanza
 * @prop handleClose        Callback for when the modal is closed
 *
 */

type SetColumnsProps = {
  refetchDashboard: () => void;
  dashboardConfig: DashboardConfigType;
  showModal: boolean;
  handleClose: () => void;
};

function SetColumns({
  refetchDashboard,
  dashboardConfig,
  showModal,
  handleClose,
}: SetColumnsProps): JSX.Element {

  const dashboardName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_NAME));
  const [layout, setLayout] = useState({ columns: 3, ...dashboardConfig.options.layout });
  const [saveLayout] = useMutation(SAVE_LAYOUT);

  const doSetColumns = (): void => {
    showWaitCursor(true);
    const graphsWithNewLayouts = setColumnLayouts(dashboardConfig.graphs as GraphConfigType[], Number(layout.columns));
    const options = trimUnusedOptions({ ...dashboardConfig.options, layout });
    saveLayout({
      variables: {
        name: dashboardName,
        graphs: JSON.stringify({ graphs: graphsWithNewLayouts }, null, 2),
        options: JSON.stringify(options, null, 2),
        tabName: dashboardConfig.tabName,
      },
    }).then(() => {
      refetchDashboard();
    });
  };

  const columns = ['1', '2', '3', '4', '5'];
  const setColumns = (newValue: string): void => setLayout({ ...layout, columns: Number(newValue) });

  return (
    <Dialog
      title="Reset Layout"
      show={showModal}
      close={handleClose}
      okCallback={doSetColumns}
      cancelCallback={handleClose}
    >
      <div className="SetColumns">
        <div className="SetColumns__Count">
          <div>Reorganize this dashboard into</div>
          <DropdownSelect values={columns} currentValue={layout.columns.toString()} callback={setColumns} testId={ELEMENT.setLayoutDialogColumns} />
          <div>
            {`column${layout.columns === 1 ? '' : 's'} across`}
          </div>
        </div>
        <div className="SetColumns__Note">
          <br />
          Please note:
          <ul>
            <li>This may change the order of existing panels</li>
            <li>The layout can still be edited until you lock it</li>
          </ul>
        </div>
      </div>
    </Dialog>
  );
}

export default SetColumns;
